<template>
    <v-container fluid class="timesheet-wrapper pa-0">
        <v-row no-gutters class="fill-height">

            <!-- ================= LEFT PANEL ================= -->
            <v-col class="left-panel-custom">
                <div class="pa-4">
                    <!-- Search -->
                      <v-text-field v-model="search_driver" dense solo clearable hide-details label="Search Drivers" />

                    <!-- Driver List -->
                    <v-list dense class="mt-4">
                        <v-list-item v-for="driver in driverListSearched" :key="driver.id"
                            @click="selectDriver(driver)" :class="{
                                'grey lighten-4': selectedDriver && selectedDriver.id === driver.id,
                                'blue--text font-weight-bold': driver.id < 0
                            }">
                            <v-list-item-content>
                                <v-list-item-title>{{ driver.full_name }}</v-list-item-title>
                                <v-list-item-subtitle v-if="driver.id > 0">{{ driver.phone }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </div>
            </v-col>

            <!-- ================= RIGHT PANEL: TIMESHEET ================= -->
            <v-col class="right-panel-custom">
                <!-- ======= TOP TOOLBAR ======= -->
                <v-toolbar flat class="top-toolbar">

                    <!-- LEFT -->
                    <div class="d-flex align-center">
                        <!-- RESTAURANT GROUP (AUTOCOMPLETE) -->
                        <v-autocomplete v-model="selectedRestaurantGroupId" :items="restaurantGroupOptions" item-text="name" item-value="id" label="All locations" dense solo clearable hide-details class="location-autocomplete mr-2">
                            <template slot="selection" slot-scope="data">
                                <div>
                                    <div v-html="data.item.name" />
                                </div>
                            </template>
                            <template slot="item" slot-scope="data">
                                <div class="select-drop-item-sub_restaurant">
                                    <div v-html="data.item.name" />
                                </div>
                            </template>
                        </v-autocomplete>

                        <!-- DATE RANGE -->
                        <div class="date-pill d-flex align-center mr-2">
                            <v-btn icon small @click="onPreviosRange">
                                <v-icon small>mdi-chevron-left</v-icon>
                            </v-btn>

                            <v-menu v-model="menu_date_picker" offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn text small v-on="on" class="px-2 text-none">
                                        {{ dateRangeText(date_range_selected) }}
                                    </v-btn>
                                </template>

                                <v-date-picker v-model="date_range_selected" range @change="dateRangeSelect" />
                            </v-menu>

                            <v-btn icon small @click="onNextRange">
                                <v-icon small>mdi-chevron-right</v-icon>
                            </v-btn>
                        </div>

                        <!-- INTERVAL -->
                        <v-select v-model="interval_selected_id" :items="interval_list" item-text="name" item-value="id"
                            dense solo flat hide-details prepend-inner-icon="mdi-calendar-blank-outline"
                            class="pill-select mr-2" />

                        <!-- STATUS -->
                        <v-select v-model="statusFilter" :items="statusOptions" item-text="label" item-value="value"
                            dense solo flat hide-details class="pill-select active" />

                    </div>

                    <v-spacer />

                    <!-- RIGHT -->
                    <div class="d-flex align-center">

                        <v-btn small outlined class="pill-btn mr-2" :disabled="selectedTimesheetIds.length === 0"
                            @click="handleBulkAction">
                            <v-icon small left>
                                {{ bulkActionMode === 'discard'
                                    ? 'mdi-delete-outline'
                                    : 'mdi-check-circle-outline'
                                }}
                            </v-icon>

                            {{ bulkActionMode === 'discard'
                                ? 'Discard Selected'
                                : 'Approve Selected'
                            }}

                            <span v-if="selectedTimesheetIds.length">
                                ({{ selectedTimesheetIds.length }})
                            </span>
                        </v-btn>
                        <v-btn small outlined class="pill-btn green mr-2">
                            <v-icon small left>mdi-table</v-icon>
                            XLS Payments
                        </v-btn>

                        <v-btn small class=" pill-btn primary-pill">
                            <v-icon small left>mdi-plus</v-icon>
                            Add Time Sheet
                        </v-btn>

                    </div>

                </v-toolbar>

                <!-- ======= TIMESHEET TABLE & SUMMARY WRAPPER ======= -->
                <div class="table-summary-wrapper">
                    <!-- TABLE SECTION -->
                    <div :class="['timesheet-content', { 'table-collapsed': selectedTimesheet }]">
                        <v-data-table :headers="tableHeaders" :items="timesheetData" :search="searchDriver" :items-per-page="-1"
                            hide-default-footer class="timesheet-table" item-key="id">
                            <template v-slot:item="{ item }">
                                <!-- DRIVER ROW -->
                                <tr class="timesheet-row" :class="{ 'selected-row': selectedTimesheet && selectedTimesheet.id === item.id, 'virtual-row': item.id < 0 }" @click="selectTimesheet(item)">
                                    <td @click.stop>
                                        <v-checkbox dense hide-details
                                            :disabled="item.id <= 0"
                                            :input-value="selectedTimesheetIds.includes(item.id)"
                                            @click.stop="toggleSelect(item)" />
                                    </td>
                                    <td>{{ item.date }}</td>
                                    <td>{{ item.deliveryGroup }}</td>
                                    <td>{{ item.driver }}</td>
                                    <td class="status-cell">
                                        <v-chip small label class="custom-chip" :class="getStatusClass(item.status)">
                                            {{ item.status }}
                                        </v-chip>
                                    </td>
                                    <td class="progress-cell">
                                        <v-chip small label class="custom-chip"
                                            :class="getProgressClass(item.progress)">
                                            {{ item.progress }}
                                        </v-chip>
                                    </td>
                                    <td class="location-cell">{{ item.location }}</td>
                                    <td class="time-cell">{{ item.time }}</td>
                                    <td class="hours-cell">{{ item.hours }}</td>
                                    <td class="earning-cell">
                                        <div class="earning-breakdown">
                                            <span class="base-earning">{{ item.baseEarning }}</span>
                                            <span v-if="item.bonus!=0" class="bonus-earning">+£{{ item.bonus }}+</span>
                                            <span class="bonus-earning">+£{{ item.fuelBonus }}</span>
                                            <span class="total-earning">{{ item.totalEarning }}</span>
                                        </div>
                                    </td>
                                    <td class="action-cell">
                                        <v-btn icon small @click.stop="expandRow(item)">
                                            <v-icon>
                                                {{expandedShiftId === item.shift_id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                            </v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                                <!-- RESTAURANT CHILD ROWS -->
                                <tr v-for="r in ( expandedShiftId === item.shift_id ? item.restaurantTimesheets : [] )" :key="r.id" class="restaurant-child-row" :class="{ 'selected-row': selectedTimesheet && selectedTimesheet.id === r.id }" @click="selectRestaurantTimesheet(r)">
                                    <!-- checkbox -->
                                    <td></td>
                                    <!-- Date → hidden -->
                                    <td></td>
                                    <!-- Delivery Group → hidden -->
                                    <td></td>
                                    <!-- Driver column → show driver name -->
                                    <td></td>
                                    <!-- Restaurant status -->
                                    <td class="status-cell">
                                        <v-chip small label class="custom-chip" :class="getStatusClass(getStatusText(r.status))"> {{ getStatusText(r.status) }}
                                        </v-chip>
                                    </td>
                                    <!-- Restaurant progress -->
                                    <td class="progress-cell">
                                        <v-chip small label class="custom-chip" :class="getProgressClass(getProgressText(r.status_progress))"> {{ getProgressText(r.status_progress) }}
                                        </v-chip>
                                    </td>
                                    <!-- Location column → show restaurant name -->
                                    <td>
                                        {{ r.restaurant ? r.restaurant.name : '-' }}
                                    </td>
                                    <!-- Restaurant time -->
                                    <td>
                                        {{ formatTimeDisplay(r.start_time) }} - {{ formatTimeDisplay(r.finish_time) }}
                                    </td>
                                    <!-- Restaurant hours -->
                                    <td>
                                        {{ calculateHours(r.start_time, r.finish_time) }}
                                    </td>
                                    <!-- Restaurant earning -->
                                    <td class="earning-cell">
                                        <div class="earning-breakdown">
                                            <span class="base-earning">£{{ ((r.cost_total - r.cost_bonus) || 0).toFixed(2) }}</span>
                                            <span v-if="r.cost_bonus != 0" class="bonus-earning">+£{{ r.cost_bonus }}+</span>
                                            <span class="bonus-earning">+£{{ r.const_fuel_bonus || 0 }}</span>
                                            <span class="total-earning">£{{ ((r.cost_total + r.const_fuel_bonus) || 0).toFixed(2) }}</span>
                                        </div>
                                    </td>
                                    <!-- expand column empty -->
                                    <td></td>
                                </tr>
                            </template>
                        </v-data-table>
                    </div>

                    <!-- ======= SUMMARY SECTION ======= -->
                    <div v-if="selectedTimesheet" class="summary-section">
                        <!-- HEADER -->
                        <div class="summary-header">
                            <h3>Timesheet Summary</h3>

                            <div class="summary-actions">
                                <!-- Unsaved / virtual row badge -->
                                <div class="virtual-badge" v-if="isRowLocked">
                                    Awaiting driver clock-in — read only
                                </div>
                                <!-- Live tracking badge -->
                                <div class="live-badge" v-if="isTrackingLive">
                                    <span class="live-dot"></span> Live tracking
                                </div>

                                <v-btn outlined small class="header-pill">
                                    <v-icon small left>mdi-map-marker-outline</v-icon>
                                    Scroll To
                                </v-btn>

                                <v-btn outlined small class="header-pill">
                                    <v-icon small left>mdi-pin-outline</v-icon>
                                    Pinned
                                </v-btn>
                            </div>
                        </div>

                        <div class="summary-body">

                            <!-- LEFT -->
                            <div class="summary-left" :class="{ 'section-disabled': isSummaryDisabled }">

                                <!-- Scheduled details -->
                                <div class="schedule-box">
                                    <div class="schedule-item">
                                        <span>Scheduled Details:</span>
                                        <strong>
                                            {{
                                                selectedTimesheetFull &&
                                                    selectedTimesheetFull.period_date
                                                    ? moment(selectedTimesheetFull.period_date).format('DD/MM/YYYY')
                                                    : '-'
                                            }}
                                        </strong>
                                    </div>
                                    <div class="schedule-item">
                                        <span>Time:</span>
                                        <strong>
                                            {{ form.start_time || '--' }}
                                            {{ form.finish_time || '--' }}
                                        </strong>
                                    </div>

                                    <div class="schedule-item">
                                        <span>Bonus:</span>
                                        <strong>+£{{ form.cost_bonus || 0 }}</strong>
                                    </div>
                                    <div class="schedule-item">
                                        <span>Fuel bonus:</span>
                                        <strong>+£{{ form.const_fuel_bonus || 0 }}</strong>
                                    </div>

                                    <div class="schedule-item">
                                        <span>Hidden bonus:</span>
                                        <strong>+£0</strong>
                                    </div>
                                </div>

                                <!-- FORM GRID -->
                                <div class="summary-form-grid">

                                    <div>
                                        <label>Delivery Group Name*</label>
                                        <v-text-field :value="selectedDeliveryGroupName" dense solo disabled
                                            hide-details />
                                    </div>

                                    <div>
                                        <label>Date*</label>
                                        <v-text-field v-model="form.period_date" dense solo type="date" hide-details :disabled="isRowLocked" />
                                    </div>

                                    <div>
                                        <label>Start Time*</label>
                                        <v-autocomplete v-model="form.start_time" :items="timeSlots" placeholder="--:--"
                                            dense solo hide-details clearable open-on-clear
                                            :disabled="isRowLocked"
                                            prepend-inner-icon="mdi-clock-outline"
                                            :menu-props="{ auto: true, maxHeight: 300 }"
                                            @focus="onTimeFocus('start_time')"
                                            @blur="formatTime('start_time')" />
                                    </div>

                                    <div>
                                        <label>End Time*</label>
                                        <v-autocomplete v-model="form.finish_time" :items="timeSlots" placeholder="--:--"
                                            dense solo hide-details clearable open-on-clear
                                            :disabled="isRowLocked"
                                            prepend-inner-icon="mdi-clock-outline"
                                            :menu-props="{ auto: true, maxHeight: 300 }"
                                            @focus="onTimeFocus('finish_time')"
                                            @blur="formatTime('finish_time')" />
                                    </div>

                                    <div>
                                        <label>Driver Bonus</label>
                                        <v-text-field v-model="form.cost_bonus" prefix="£" dense solo hide-details :disabled="isRowLocked" />
                                    </div>

                                    <div>
                                        <label>Fuel Bonus</label>
                                        <v-text-field v-model="form.const_fuel_bonus" prefix="£" dense solo
                                            hide-details readonly />
                                    </div>
                                </div>

                                <!-- MILESTONES -->
                                <div class="milestone-row">
                                    <div class="milestone-item">
                                        <label>0-3</label>
                                        <v-text-field v-model="form.fuel_bonus_0"  dense solo
                                            :disabled="!isFuelBonusEditable" @input="calculateBonus" hide-details />
                                    </div>

                                    <div class="milestone-item">
                                        <label>3-6</label>
                                        <v-text-field v-model="form.fuel_bonus_3"  dense solo
                                            :disabled="!isFuelBonusEditable" @input="calculateBonus" hide-details />
                                    </div>

                                    <div class="milestone-item">
                                        <label>6+</label>
                                        <v-text-field v-model="form.fuel_bonus_6"  dense solo
                                            :disabled="!isFuelBonusEditable" @input="calculateBonus" hide-details />
                                    </div>
                                </div>

                                <!-- COMMENT -->
                                <div class="comment-section">
                                    <label>Driver's Comment</label>
                                    <v-textarea v-model="form.comment" solo rows="3" hide-details :disabled="isRowLocked" />
                                </div>
                            </div>

                            <!-- RIGHT -->
                            <div class="summary-right">
                                <div class="button-grid" :class="{ 'section-disabled': isSummaryDisabled }">
                                    <v-btn class="btn-approve" :disabled="isApproved() || isRowLocked" @click="approveTimesheet" >
                                        <v-icon left>mdi-check</v-icon>
                                        {{ isApproved() ? 'Already Approved' : 'Approve Timesheet' }}
                                    </v-btn>
                                    <v-btn class="btn-remove" :disabled="isRowLocked" @click="removeTimesheet" >
                                        <v-icon left>mdi-delete-outline</v-icon>
                                        Remove
                                    </v-btn>

                                    <v-btn class="btn-discard" :disabled="isDiscarded() || isRowLocked" @click="discardTimesheet" >
                                        <v-icon left>mdi-trash-can-outline</v-icon>
                                        {{ isDiscarded() ? 'Already Discarded' : 'Discard' }}
                                    </v-btn>

                                    <v-btn class="btn-pay" :disabled="isRowLocked" @click="unapprovePay" >
                                        <v-icon left>mdi-currency-usd</v-icon>
                                        Unapprove Pay
                                    </v-btn>

                                    <v-btn class="btn-time" :disabled="isRowLocked" @click="unapproveTime" >
                                        <v-icon left>mdi-clock-outline</v-icon>
                                        Unapprove Time
                                    </v-btn>
                                </div>
                                <!-- Map (never dimmed/disabled — live tracking must stay usable) -->
                                <div class="map-box-wrapper">
                                    <div id="leafletMap" class="map-box"></div>
                                    <div v-if="isTrackingLive && isLiveLocationLoading" class="map-loading-overlay">
                                        <div class="map-loading-spinner"></div>
                                        <span>Finding live location…</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import moment from 'moment'
import * as _ from 'lodash'
import { mapGetters } from 'vuex'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import pickupIcon from '@/assets/pickup.svg'
import deliveryIcon from '@/assets/delivery.svg'
import restaurantIconSvg from '@/assets/restaurant-marker.svg'
import waitingIconSvg from '@/assets/waiting-location-marker.svg'
import liveIconSvg from '@/assets/live.png'
import {
    DATE_RANGE_HELPERS,
    INTERVAL_DAY,
    INTERVAL_WEEK,
    INTERVAL_TWO_WEEK,
    INTERVAL_MONTH,
} from '@/store/shifts'

// Constants
const TIMESHEET_STATUS = {
    PENDING: 10,
    DISCARDED: 20,
    APPROVED: 30,
}

const TIMESHEET_PROGRESS = {
    IN_WORK: 10,
    SUBMITED: 20,
    NOT_SUBMITED: 30, // virtual/unfilled shift — no timesheet row exists in the DB yet
    PAY_APPROVED: 40,
    TIME_APPROVED: 50,
    DISCARDED: 60,
    DISCARDED_BY_DRIVER: 70,
}
const TIMESHEET_TYPE = {
    DRIVER: 10,
    RESTAURANT: 20,
}
// How often to poll the driver's live location while a shift is in progress
const LIVE_POLL_INTERVAL_MS = 15000

export default {
    name: 'DriverShareTimesheet',

    data() {
        return {
            moment,
            TIMESHEET_STATUS,
            TIMESHEET_PROGRESS,
            TIMESHEET_TYPE,
            selectedTimesheetIds: [],
            searchDriver: '',
            search_driver: '',
            menu_date_picker: false,
            selectedDriver: null,
            selectedRestaurantGroupId: null,
            selectedTimesheet: null,
            expandedShiftId: null,
            interval_selected_id: INTERVAL_WEEK,
            statusFilter: 'all',
            form: {
                restaurant_group_id: null,
                period_date: null,
                start_time: null,
                finish_time: null,
                cost_bonus: 0,
                const_fuel_bonus: 0,
                fuel_bonus_0: 0,
                fuel_bonus_3: 0,
                fuel_bonus_6: 0,
                comment: ''
            },
            fuel_bonus_0_default: 0,
            fuel_bonus_3_default: 0,
            fuel_bonus_6_default: 0,
            driver_rate_default: 0,
            restaurant_sharing_rates: { 1: 0, 2: 0, 3: 0 },

            interval_list: [
                { id: INTERVAL_DAY, name: 'Day' },
                { id: INTERVAL_WEEK, name: 'Week' },
                { id: INTERVAL_TWO_WEEK, name: '2-Week' },
                { id: INTERVAL_MONTH, name: 'Month' },
            ],

            date_range_selected: [
                moment().startOf('isoWeek').format('YYYY-MM-DD'),
                moment().endOf('isoWeek').format('YYYY-MM-DD'),
            ],

            statusOptions: [
                { label: 'All', value: 'all' },
                { label: 'Pending', value: 'pending' },
                { label: 'Approved', value: 'approved' },
                { label: 'Discarded', value: 'discarded' },
            ],

            tableHeaders: [
                { text: '', value: 'select', sortable: false, width: 40 },
                { text: 'Date', value: 'date' },
                { text: 'Delivery Group', value: 'deliveryGroup' },
                { text: 'Driver', value: 'driver' },
                { text: 'Status', value: 'status' },
                { text: 'Progress', value: 'progress' },
                { text: 'Location', value: 'location' },
                { text: 'Time', value: 'time' },
                { text: 'Hours', value: 'hours' },
                { text: 'Earned(+£Bonus)', value: 'earning' },
                { text: '', value: 'action', sortable: false },
            ],
            // Map icons
            restaurantIcon: null,
            waitingIcon: null,
            pickupMarkerIcon: null,
            deliveryMarkerIcon: null,
            // Live tracking state
            liveIcon: null,
            liveMarker: null,
            liveLocationInterval: null,
            isTrackingLive: false,
            hasCenteredOnLive: false,
            isLiveLocationLoading: false,
        }
    },

    mounted() {
        this.initIcons()
        Promise.all([
            this.$store.dispatch('fetchLocations'),
            this.$store.dispatch('fetchDrivers'),
            this.$store.dispatch('fetchRestaurantGroups'),
            this.$store.dispatch('fetchOptions'),

        ]).then(() => {
            // Load fuel bonus rates (£ per delivery per bucket) set by admin
            const options = this.$store.getters['options'] || []
            const fuelBonus0 = options.find(o => o.okey === 'drivershare_fuel_bonus_0')
            const fuelBonus3 = options.find(o => o.okey === 'drivershare_fuel_bonus_3')
            const fuelBonus6 = options.find(o => o.okey === 'drivershare_fuel_bonus_6')

            if (fuelBonus0 && fuelBonus0.ovalue) this.fuel_bonus_0_default = Number(fuelBonus0.ovalue)
            if (fuelBonus3 && fuelBonus3.ovalue) this.fuel_bonus_3_default = Number(fuelBonus3.ovalue)
            if (fuelBonus6 && fuelBonus6.ovalue) this.fuel_bonus_6_default = Number(fuelBonus6.ovalue)

            // Driver flat rate — same option key the backend reads in start()
            const driverRate = options.find(o => o.okey === 'drivershare_drivers_standard_pay')
            if (driverRate && driverRate.ovalue) this.driver_rate_default = Number(driverRate.ovalue)

            // Restaurant sharing rates — same option keys calculateRestaurantSharingCost() reads
            const rate1 = options.find(o => o.okey === 'drivershare_restaurant_cost_per_hour')
            const rate2 = options.find(o => o.okey === 'drivershare_restaurant_count_2')
            const rate3 = options.find(o => o.okey === 'drivershare_restaurant_count_3')
            this.restaurant_sharing_rates = {
                1: rate1 && rate1.ovalue ? Number(rate1.ovalue) : 0,
                2: rate2 && rate2.ovalue ? Number(rate2.ovalue) : 0,
                3: rate3 && rate3.ovalue ? Number(rate3.ovalue) : 0,
            }
            const startDate = moment().startOf('isoWeek').format('YYYY-MM-DD')
            const endDate = moment().endOf('isoWeek').format('YYYY-MM-DD')
            this.fetchTimesheetForRange(startDate, endDate)
            this.initMap()
        })
    },

    beforeDestroy() {
        this.stopLiveTracking()
        if (this._map) {
            this._map.remove()
            this._map = null
        }
    },
    watch: {
        interval_selected_id() {
            let start, end

            switch (this.interval_selected_id) {
                case INTERVAL_DAY:
                    start = moment().format('YYYY-MM-DD')
                    end = start
                    break

                case INTERVAL_WEEK:
                    start = moment().startOf('isoWeek')
                    end = moment().endOf('isoWeek')
                    break

                case INTERVAL_TWO_WEEK:
                    start = moment().startOf('isoWeek')
                    end = moment().add(1, 'week').endOf('isoWeek')
                    break

                case INTERVAL_MONTH:
                    start = moment().startOf('month')
                    end = moment().endOf('month')
                    break
            }

            this.date_range_selected = [
                moment(start).format('YYYY-MM-DD'),
                moment(end).format('YYYY-MM-DD')
            ]

            this.fetchTimesheetForRange(this.date_range_selected[0], this.date_range_selected[1])
        },
        'relatedDriverTimesheet.status_progress'(status) {
            if (status === TIMESHEET_PROGRESS.IN_WORK) {
                this.startLiveTracking()
            } else {
                this.stopLiveTracking()
            }
        },
    },

    computed: {
        ...mapGetters([
            'drivers',
            'restaurantGroups',
            'drivershareTimesheets',
            'drivershareShifts', 
            'options'
        ]),
        bulkActionMode() {
            if (!this.selectedTimesheetIds.length) {
                return 'approve'
            }
            const selected = this.allTimesheets.filter(
                t => this.selectedTimesheetIds.includes(t.id)
            )
            const allApproved = selected.every(
                t => t.status === TIMESHEET_STATUS.APPROVED
            )
            return allApproved ? 'discard' : 'approve'
        },
        // Options for the toolbar restaurant-group autocomplete
        restaurantGroupOptions() {
            return _.map(this.restaurantGroups, g => ({ id: g.id, name: g.name }))
        },
        selectedDeliveryGroupName() {
            if (!this.selectedTimesheetFull) return '-'
            let groupId = null

            if (this.selectedTimesheetFull.shift && this.selectedTimesheetFull.shift.restaurant_group_id) {
                groupId = this.selectedTimesheetFull.shift.restaurant_group_id
            } else if (this.selectedTimesheet && this.selectedTimesheet.shift && this.selectedTimesheet.shift.restaurant_group_id) {
                groupId = this.selectedTimesheet.shift.restaurant_group_id
            } else {
                groupId = this.form.restaurant_group_id
            }

            return this.restaurantGroupMap[groupId] || '-'
        },
        isRowLocked() {
            return !this.selectedTimesheetFull || this.selectedTimesheetFull.id <= 0
        },
        isSummaryDisabled() {
            if (!this.selectedTimesheetFull) return false
            if (this.isRowLocked) return true

            const status = this.selectedTimesheetFull.status
            const progress = this.selectedTimesheetFull.status_progress

            return (
                status === TIMESHEET_STATUS.PENDING &&
                progress === TIMESHEET_PROGRESS.IN_WORK
            )
        },
        isEditable() {
            if (!this.selectedTimesheetFull) return false
            if (this.isRowLocked) return false

            return this.selectedTimesheetFull.status !== TIMESHEET_STATUS.APPROVED
        },
        isFuelBonusEditable() {
            if (!this.selectedTimesheetFull) return false
            if (!this.isEditable) return false

            return this.selectedTimesheetFull.type === TIMESHEET_TYPE.RESTAURANT
        },
        virtualDriverTimesheets() {
            const realShiftIds = new Set(
                this.drivershareTimesheets
                    .filter(t => t.type === TIMESHEET_TYPE.DRIVER)
                    .map(t => t.shift_id)
            )

            return (this.drivershareShifts || [])
                .filter(shift => !realShiftIds.has(shift.id))
                .map(shift => {
                    const startTime = shift.driver_start_time || shift.start_time
                    const finishTime = shift.driver_finish_time || shift.finish_time
                    const hours = this.getHoursNumeric(startTime, finishTime)
                    let cost_total = (Number(this.driver_rate_default) || 0) * hours
                    if (shift.cost_bonus) {
                        cost_total += Number(shift.cost_bonus)
                    }
                    if (shift.const_fuel_bonus) {
                        cost_total += Number(shift.const_fuel_bonus)
                    }
                    return {
                        id: -shift.id,
                        shift_id: shift.id,
                        type: TIMESHEET_TYPE.DRIVER,
                        driver_id: shift.driver_id || null,
                        driver: shift.driver || null,
                        status: TIMESHEET_STATUS.PENDING,
                        status_progress: TIMESHEET_PROGRESS.NOT_SUBMITED,
                        period_date: shift.period_date,
                        driver_start_time: startTime,
                        driver_finish_time: finishTime,
                        start_time: startTime,
                        finish_time: finishTime,
                        cost_per_hour: this.driver_rate_default,
                        cost_bonus: shift.cost_bonus || 0,
                        const_fuel_bonus: shift.const_fuel_bonus || 0,
                        cost_extra_bonus: 0,
                        fuel_bonus_0: 0,
                        fuel_bonus_3: 0,
                        fuel_bonus_6: 0,
                        cost_total: cost_total,
                        comment: shift.comment || '',
                        shift: shift,
                    }
                })
        },
        virtualRestaurantTimesheets() {
            const realKeys = new Set(this.drivershareTimesheets.filter(t => t.type === TIMESHEET_TYPE.RESTAURANT).map(t => `${t.shift_id}-${t.restaurant_id}`))
            const result = []

            ;(this.drivershareShifts || []).forEach(shift => {
                if (!Array.isArray(shift.shifts) || shift.shifts.length === 0) return

                const legs = shift.shifts
                    .map(leg => ({
                        restaurant_id: leg.restaurant_id || (leg.restaurant && leg.restaurant.id),
                        start_time: leg.start_time,
                        finish_time: leg.finish_time,
                        restaurant: leg.restaurant || null,
                        cost_bonus: leg.cost_bonus || 0,
                        comment: leg.comment || '',
                    }))
                    .filter(l => l.restaurant_id)

                if (!legs.length) return
                const sharingCosts = this.calculateRestaurantSharingCosts(legs)

                legs.forEach(leg => {
                    const key = `${shift.id}-${leg.restaurant_id}`
                    if (realKeys.has(key)) return 

                    result.push({
                        id: -(shift.id * 1000 + Number(leg.restaurant_id)),
                        shift_id: shift.id,
                        type: TIMESHEET_TYPE.RESTAURANT,
                        restaurant_id: leg.restaurant_id,
                        restaurant: leg.restaurant,
                        status: TIMESHEET_STATUS.PENDING,
                        status_progress: TIMESHEET_PROGRESS.NOT_SUBMITED,
                        period_date: shift.period_date,
                        start_time: leg.start_time,
                        finish_time: leg.finish_time,
                        cost_bonus: leg.cost_bonus,
                        const_fuel_bonus: 0,
                        cost_extra_bonus: 0,
                        fuel_bonus_0: 0,
                        fuel_bonus_3: 0,
                        fuel_bonus_6: 0,
                        cost_total: sharingCosts[leg.restaurant_id] || 0,
                        comment: leg.comment,
                        shift: shift,
                    })
                })
            })

            return result
        },
        allTimesheets() {
            return this.drivershareTimesheets
                .concat(this.virtualDriverTimesheets)
                .concat(this.virtualRestaurantTimesheets)
        },

        selectedTimesheetFull() {
            if (!this.selectedTimesheet) return null

            return this.allTimesheets.find(
                t => t.id === this.selectedTimesheet.id
            )
        },
        relatedDriverTimesheet() {
            if (!this.selectedTimesheetFull) return null
            if (this.selectedTimesheetFull.type === TIMESHEET_TYPE.DRIVER) {
                return this.selectedTimesheetFull
            }
            return this.allTimesheets.find(
                t => t.shift_id === this.selectedTimesheetFull.shift_id &&
                     t.type === TIMESHEET_TYPE.DRIVER
            ) || null
        },
        liveDriver() {
            if (!this.relatedDriverTimesheet) return null
            return this.relatedDriverTimesheet.driver ||
                (this.relatedDriverTimesheet.shift && this.relatedDriverTimesheet.shift.driver) ||
                null
        },
        isDriverWorking() {
            return !!(
                this.relatedDriverTimesheet &&
                this.relatedDriverTimesheet.status_progress === TIMESHEET_PROGRESS.IN_WORK
            )
        },
        stopsToShow() {
            const full = this.selectedTimesheetFull
            if (!full || !full.shift || !Array.isArray(full.shift.routes)) {
                return []
            }

            let stops = []
            full.shift.routes.forEach(route => {
                if (Array.isArray(route.stops)) {
                    stops = stops.concat(route.stops)
                }
            })

            if (full.type === TIMESHEET_TYPE.RESTAURANT) {
                stops = stops.filter(s => s.restaurant_id === full.restaurant_id)
            }

            return stops
        },
        restaurantsToShow() {
            const full = this.selectedTimesheetFull
            if (!full || !full.shift || !Array.isArray(full.shift.shifts)) {
                return []
            }

            let restaurants = full.shift.shifts
                .map(s => s.restaurant)
                .filter(Boolean)

            if (full.type === TIMESHEET_TYPE.RESTAURANT) {
                restaurants = restaurants.filter(r => r.id === full.restaurant_id)
            }

            const seen = new Set()
            return restaurants.filter(r => {
                if (seen.has(r.id)) return false
                seen.add(r.id)
                return true
            })
        },
        waitingLocation() {
            const full = this.selectedTimesheetFull
            const group = full && full.shift ? full.shift.restaurant_group : null
            if (!group || !group.waiting_lat || !group.waiting_lng) return null
            return group
        },

        // restaurantGroup map
        restaurantGroupMap() {
            const map = {}
            this.restaurantGroups.forEach(function (g) {
                map[g.id] = g.name
            })
            return map
        },

        //Driver List
        driver_list() {
            return this.drivers
        },
        timeSlots() {
            const slots = []
            for (let h = 0; h < 24; h++) {
                for (let m = 0; m < 60; m += 15) {
                    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
                }
            }
            return slots
        },

        driverListSearched() {
            let list = _.map(this.driver_list, p => p)

            if (this.search_driver) {
                list = _.filter(list, p =>
                    p.full_name &&
                    p.full_name.toLowerCase().includes(this.search_driver.toLowerCase())
                )
            }

            if (list.length === 0 || (list.length > 0 && list[0].id !== null)) {
                list.unshift({ id: -1, full_name: `All drivers [${list.length}]` })
            }

            return list
        },

        //Timesheet Table Data
        timesheetData() {
            const vm = this

            // only DRIVER timesheets shown in main table — real rows from the
            let driverTimesheets = this.drivershareTimesheets
                .filter(t => t.type === TIMESHEET_TYPE.DRIVER)
                .concat(this.virtualDriverTimesheets)

            // filter by selected driver (left panel)
            if (this.selectedDriver && this.selectedDriver.id > 0) {
                driverTimesheets = driverTimesheets.filter(
                    t => t.driver_id === this.selectedDriver.id
                )
            }

            // filter by selected restaurant group (toolbar autocomplete)
            if (this.selectedRestaurantGroupId) {
                driverTimesheets = driverTimesheets.filter(t =>
                    t.shift && t.shift.restaurant_group_id === this.selectedRestaurantGroupId
                )
            }

            let data = driverTimesheets.map(driverTs => {
                const shift = driverTs.shift || {}
                const driver = driverTs.driver || {}
                const groupName = vm.restaurantGroupMap[ shift.restaurant_group_id ] || '-'
                // restaurant timesheets belonging to same shift — real DB rows
                const restaurantTimesheets =
                    vm.drivershareTimesheets
                        .filter(t =>
                            t.shift_id === driverTs.shift_id &&
                            t.type === TIMESHEET_TYPE.RESTAURANT
                        )
                        .concat(
                            vm.virtualRestaurantTimesheets.filter(
                                t => t.shift_id === driverTs.shift_id
                            )
                        )

                return {
                    id: driverTs.id,
                    shift_id: driverTs.shift_id,
                    rawStatus: driverTs.status,

                    date: driverTs.period_date
                        ? moment(driverTs.period_date)
                            .format('DD/MM/YYYY')
                        : '-',

                    deliveryGroup: groupName,

                    driver:
                        (driver.first_name || '') +
                        ' ' +
                        (driver.last_name || ''),

                    status:
                        vm.getStatusText(driverTs.status),

                    progress:
                        vm.getProgressText(
                            driverTs.status_progress
                        ),

                    location: '—',

                    time:
                        vm.formatTimeDisplay(driverTs.driver_start_time)
                        + ' - ' +
                        vm.formatTimeDisplay(driverTs.driver_finish_time),

                    hours: vm.calculateHours(
                        driverTs.driver_start_time,
                        driverTs.driver_finish_time
                    ),

                    baseEarning:
                        '£' + ((driverTs.cost_total - driverTs.cost_bonus) || 0).toFixed(2),

                    bonus: driverTs.cost_bonus || 0,
                    fuelBonus: driverTs.const_fuel_bonus || 0,
                    totalEarning:
                        '£' + ((driverTs.cost_total + driverTs.const_fuel_bonus) || 0).toFixed(2),

                    restaurantTimesheets
                }
            })

            // status filter
            if (this.statusFilter !== 'all') {
                data = data.filter(item => {
                    switch (this.statusFilter) {
                        case 'pending':
                            return item.rawStatus === TIMESHEET_STATUS.PENDING

                        case 'approved':
                            return item.rawStatus === TIMESHEET_STATUS.APPROVED

                        case 'discarded':
                            return item.rawStatus === TIMESHEET_STATUS.DISCARDED

                        default:
                            return true
                    }
                })
            }

            return data
        },
    },

    methods: {
        // Date Range Navigation
        onPreviosRange() {
            const [start, end] = this.date_range_selected

            let newStart, newEnd

            switch (this.interval_selected_id) {
                case INTERVAL_DAY:
                    newStart = moment(start).subtract(1, 'day')
                    newEnd = moment(end).subtract(1, 'day')
                    break

                case INTERVAL_WEEK:
                    newStart = moment(start).subtract(1, 'week')
                    newEnd = moment(end).subtract(1, 'week')
                    break

                case INTERVAL_TWO_WEEK:
                    newStart = moment(start).subtract(2, 'week')
                    newEnd = moment(end).subtract(2, 'week')
                    break

                case INTERVAL_MONTH:
                    newStart = moment(start).subtract(1, 'month')
                    newEnd = moment(end).subtract(1, 'month')
                    break
            }

            this.date_range_selected = [
                newStart.format('YYYY-MM-DD'),
                newEnd.format('YYYY-MM-DD')
            ]

            this.fetchTimesheetForRange(this.date_range_selected[0], this.date_range_selected[1])
        },

        onNextRange() {
            const [start, end] = this.date_range_selected

            let newStart, newEnd

            switch (this.interval_selected_id) {
                case INTERVAL_DAY:
                    newStart = moment(start).add(1, 'day')
                    newEnd = moment(end).add(1, 'day')
                    break

                case INTERVAL_WEEK:
                    newStart = moment(start).add(1, 'week')
                    newEnd = moment(end).add(1, 'week')
                    break

                case INTERVAL_TWO_WEEK:
                    newStart = moment(start).add(2, 'week')
                    newEnd = moment(end).add(2, 'week')
                    break

                case INTERVAL_MONTH:
                    newStart = moment(start).add(1, 'month')
                    newEnd = moment(end).add(1, 'month')
                    break
            }

            this.date_range_selected = [
                newStart.format('YYYY-MM-DD'),
                newEnd.format('YYYY-MM-DD')
            ]

            this.fetchTimesheetForRange(this.date_range_selected[0], this.date_range_selected[1])
        },

        dateRangeSelect() {
            if (
                this.date_range_selected &&
                this.date_range_selected[0] &&
                this.date_range_selected[1]
            ) {
                this.fetchTimesheetForRange(this.date_range_selected[0],
                    this.date_range_selected[1])
            }
        },

        // ================= FETCH =================
        fetchTimesheetForRange(from, to) {
            this.$store.dispatch('fetchDrivershareTimesheets', {
                period_date_from: from,
                period_date_to: to,
                with_driver: 1,
                with_restaurant: 1,
                with_shift: 1,
            })
            this.$store.dispatch('fetchDrivershareShifts', {
                period_date_from: from,
                period_date_to: to,
                filter_draft: '1',
            })
        },

        // Status & Status Progress
        getStatusText(status) {
            switch (status) {
                case TIMESHEET_STATUS.PENDING: return 'Pending'
                case TIMESHEET_STATUS.DISCARDED: return 'Discarded'
                case TIMESHEET_STATUS.APPROVED: return 'Approved'
                default: return 'Unknown'
            }
        },

        getProgressText(progress) {
            switch (progress) {
                case TIMESHEET_PROGRESS.IN_WORK: return 'In Work'
                case TIMESHEET_PROGRESS.SUBMITED: return 'Submitted'
                case TIMESHEET_PROGRESS.NOT_SUBMITED: return 'Not Submitted'
                case TIMESHEET_PROGRESS.PAY_APPROVED: return 'Pay Approved'
                case TIMESHEET_PROGRESS.TIME_APPROVED: return 'Time Approved'
                case TIMESHEET_PROGRESS.DISCARDED: return 'Discarded'
                case TIMESHEET_PROGRESS.DISCARDED_BY_DRIVER: return 'Driver Discarded'
                default: return 'Unknown'
            }
        },

        // Hours as a raw number (used for cost_total math on virtual rows)
        getHoursNumeric(start, end) {
            if (!start || !end) return 0

            let s = moment(start, 'HH:mm')
            let e = moment(end, 'HH:mm')

            if (!s.isValid() || !e.isValid()) return 0

            if (e.isBefore(s)) {
                e.add(1, 'day') // overnight fix
            }

            return moment.duration(e.diff(s)).asHours()
        },
        // Hours, formatted for display
        calculateHours(start, end) {
            if (!start || !end) return '0 Hrs'

            return this.getHoursNumeric(start, end).toFixed(2) + ' Hrs'
        },
        formatTimeDisplay(time) {
            if (!time) return '--'
            return String(time).slice(0, 5)
        },
        timeToSeconds(time) {
            if (!time) return 0
            const m = moment(time, 'HH:mm:ss')
            if (!m.isValid()) return 0
            return m.hours() * 3600 + m.minutes() * 60 + m.seconds()
        },
        calculateRestaurantSharingCosts(legs) {
            const rates = this.restaurant_sharing_rates
            const parsed = legs.map(leg => {
                let start = this.timeToSeconds(leg.start_time)
                let finish = this.timeToSeconds(leg.finish_time)
                if (finish <= start) {
                    finish += 86400 
                }
                return { restaurant_id: leg.restaurant_id, start, finish }
            })

            const totals = {}
            parsed.forEach(leg => {
                totals[leg.restaurant_id] = totals[leg.restaurant_id] || 0
            })

            const points = Array.from(
                new Set(parsed.flatMap(leg => [leg.start, leg.finish]))
            ).sort((a, b) => a - b)

            for (let i = 0; i < points.length - 1; i++) {
                const segmentStart = points[i]
                const segmentEnd = points[i + 1]
                if (segmentEnd <= segmentStart) continue

                const activeLegs = parsed.filter(
                    leg => leg.start <= segmentStart && leg.finish >= segmentEnd
                )
                const sharingCount = activeLegs.length
                if (sharingCount === 0) continue

                const rate = rates[sharingCount] !== undefined
                    ? rates[sharingCount]
                    : (rates[3] || 0)
                const hours = (segmentEnd - segmentStart) / 3600
                const segmentCost = hours * rate

                activeLegs.forEach(leg => {
                    totals[leg.restaurant_id] += segmentCost
                })
            }

            Object.keys(totals).forEach(id => {
                totals[id] = Math.round(totals[id] * 100) / 100
            })

            return totals
        },
        handleBulkAction() {
            if (this.bulkActionMode === 'discard') {
                this.discardSelectedTimesheets()
            } else {
                this.approveSelectedTimesheets()
            }
        },
        selectDriver(driver) {
            if (driver.id === -1) {
                this.selectedDriver = null
                return
            }

            if (this.selectedDriver && this.selectedDriver.id === driver.id) {
                this.selectedDriver = null // toggle off
            } else {
                this.selectedDriver = driver
            }
        },

        // Selected Timesheet
        selectTimesheet(item) {
            // if same row clicked again → close summary
            if (this.selectedTimesheet && this.selectedTimesheet.id === item.id) {
                this.selectedTimesheet = null
                this.stopLiveTracking()
                return
            }
            this.selectedTimesheet = item

            const full = this.allTimesheets.find( t => t.id === item.id)

            if (full) {
                this.populateForm(full)
            }
            this.$nextTick(() => {
                this.initMap()
                this.syncLiveTracking()
            })
        },
        selectRestaurantTimesheet(restaurantTs) {

            if (this.selectedTimesheet && this.selectedTimesheet.id === restaurantTs.id) {
                this.selectedTimesheet = null
                this.stopLiveTracking()
                return
            }
            this.selectedTimesheet = restaurantTs
            this.form = {
                restaurant_group_id:
                    restaurantTs.shift && restaurantTs.shift.restaurant_group_id
                        ? restaurantTs.shift.restaurant_group_id
                        : null,
                period_date: restaurantTs.period_date ? moment( restaurantTs.period_date ).format('YYYY-MM-DD') : null,
                start_time: this.timeToApproximation(restaurantTs.start_time) || null,
                finish_time: this.timeToApproximation(restaurantTs.finish_time) || null,
                cost_bonus: restaurantTs.cost_bonus || 0,
                const_fuel_bonus: restaurantTs.const_fuel_bonus || 0,
                fuel_bonus_0: restaurantTs.fuel_bonus_0 || 0,
                fuel_bonus_3: restaurantTs.fuel_bonus_3 || 0,
                fuel_bonus_6: restaurantTs.fuel_bonus_6 || 0,
                comment: restaurantTs.comment || ''
            }

            this.$nextTick(() => {
                this.initMap()
                this.syncLiveTracking()
            })
        },
        populateForm(timesheet) {
            this.form = {
                restaurant_group_id:
                    timesheet.shift && timesheet.shift.restaurant_group_id
                        ? timesheet.shift.restaurant_group_id
                        : null,
                period_date:
                    timesheet.period_date
                        ? moment(timesheet.period_date).format('YYYY-MM-DD')
                        : null,

                start_time:
                    this.timeToApproximation(
                        timesheet.type === TIMESHEET_TYPE.DRIVER
                            ? timesheet.driver_start_time
                            : timesheet.start_time
                    ),

                finish_time:
                   this.timeToApproximation(
                        timesheet.type === TIMESHEET_TYPE.DRIVER
                            ? timesheet.driver_finish_time
                            : timesheet.finish_time
                    ),

                cost_bonus: timesheet.cost_bonus || 0,
                const_fuel_bonus: timesheet.const_fuel_bonus || 0,
                fuel_bonus_0: timesheet.fuel_bonus_0 || 0,
                fuel_bonus_3: timesheet.fuel_bonus_3 || 0,
                fuel_bonus_6: timesheet.fuel_bonus_6 || 0,
                comment: timesheet.comment || ''
            }
        },

        expandRow(item) {
            if (this.expandedShiftId === item.shift_id) {
                this.expandedShiftId = null
            } else {
                this.expandedShiftId = item.shift_id
            }
        },
        timeToApproximation(time_src) {
            if (!time_src) return time_src

            const time_src_m = moment(time_src, 'HH:mm:ss')
            if (!time_src_m.isValid()) {
                return time_src
            }

            const time_item = this.timeSlots.find(slot => {
                const slot_m = moment(slot, 'HH:mm')
                const diff_hours = (slot_m - time_src_m) / (1000 * 60 * 60)
                return diff_hours > -0.125
            })

            return time_item || time_src
        },

        // ================= MAP =================
        initIcons() {
            this.restaurantIcon = L.icon({
                iconUrl: restaurantIconSvg,
                iconSize: [46, 46],
                iconAnchor: [16, 32],
                popupAnchor: [0, -28],
            })
            this.waitingIcon = L.icon({
                iconUrl: waitingIconSvg,
                iconSize: [46, 46],
                iconAnchor: [19, 38],
                popupAnchor: [0, -34],
            })
            this.pickupMarkerIcon = L.icon({
                iconUrl: pickupIcon,
                iconSize: [30, 30],
                iconAnchor: [15, 30],
                popupAnchor: [0, -26],
            })
            this.deliveryMarkerIcon = L.icon({
                iconUrl: deliveryIcon,
                iconSize: [30, 30],
                iconAnchor: [15, 30],
                popupAnchor: [0, -26],
            })
            this.liveIcon = L.icon({
                iconUrl: liveIconSvg,
                iconSize: [45, 45],
                iconAnchor: [22, 45],
                popupAnchor: [0, -40],
            })
        },

        initMap() {
            // prevent multiple init
            if (this._map) {
                this._map.remove()
                this._map = null
            }
            this.liveMarker = null
            this.hasCenteredOnLive = false
            this.isLiveLocationLoading = false

            this._map = L.map('leafletMap').setView([51.505, -0.09], 13)

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: ''
            }).addTo(this._map)

            const latLngs = []

            // Restaurant markers
            this.restaurantsToShow.forEach(restaurant => {
                const lat = parseFloat(restaurant.map_latitude)
                const lng = parseFloat(restaurant.map_longitude)
                if (isNaN(lat) || isNaN(lng)) return

                L.marker([lat, lng], { icon: this.restaurantIcon })
                    .addTo(this._map)
                    .bindPopup(`<strong>${restaurant.name}</strong>`)

                latLngs.push([lat, lng])
            })

            // Waiting location marker
            if (this.waitingLocation) {
                const lat = parseFloat(this.waitingLocation.waiting_lat)
                const lng = parseFloat(this.waitingLocation.waiting_lng)
                if (!isNaN(lat) && !isNaN(lng)) {
                    L.marker([lat, lng], { icon: this.waitingIcon })
                        .addTo(this._map)
                        .bindPopup(`<strong>${this.waitingLocation.name} - Waiting Location</strong>`)

                    latLngs.push([lat, lng])
                }
            }

            // Pickup / dropoff stop markers
            this.stopsToShow.forEach(stop => {
                const lat = parseFloat(stop.latitude)
                const lng = parseFloat(stop.longitude)
                if (isNaN(lat) || isNaN(lng)) return

                const icon = stop.type === 'pickup' ? this.pickupMarkerIcon : this.deliveryMarkerIcon

                L.marker([lat, lng], { icon })
                    .addTo(this._map)
                    .bindPopup(this.buildStopPopupHtml(stop))

                latLngs.push([lat, lng])
            })

            if (latLngs.length) {
                this._map.fitBounds(latLngs, { padding: [30, 30] })
            }
        },

        buildStopPopupHtml(stop) {
            const label = stop.type === 'pickup' ? 'Pickup' : 'Drop-off'
            const note = stop.delivery_note ? stop.delivery_note : '-'
            const completed = stop.completed_at
                ? moment(stop.completed_at).format('DD/MM/YYYY HH:mm')
                : 'Not completed'

            return `
                <div style="font-size:12px;line-height:1.5;">
                    <strong>${label} · Order #${stop.order_id}</strong><br/>
                    Sequence: ${stop.sequence}<br/>
                    Completed: ${completed}<br/>
                    Note: ${note}
                </div>
            `
        },

        // ================= LIVE DRIVER TRACKING =================
        applyLiveLatLng(latlng, instant) {
            if (this.liveMarker) {
                this.liveMarker.setLatLng(latlng)
                if (!this._map.getBounds().contains(latlng)) {
                    this._map.panTo(latlng)
                }
            } else {
                this.liveMarker = L.marker(latlng, { icon: this.liveIcon, zIndexOffset: 1000 })
                    .addTo(this._map)
                    .bindPopup(
                        `<strong>${this.liveDriver.first_name} ${this.liveDriver.last_name}</strong><br>Live location`
                    )
            }
            if (!this.hasCenteredOnLive) {
                this.hasCenteredOnLive = true
                if (instant) {
                    this._map.setView(latlng, 16)
                } else {
                    this._map.flyTo(latlng, 16, { duration: 0.75 })
                }
            }

            this.isLiveLocationLoading = false
        },
        async fetchLiveLocation() {
            if (!this.liveDriver || !this._map) return
            const cached = this.$store.getters['driverLocations']
            const cachedPos = cached ? cached[this.liveDriver.id] : null
            if (!this.liveMarker && cachedPos && cachedPos.latitude && cachedPos.longitude) {
                this.applyLiveLatLng(
                    [Number(cachedPos.latitude), Number(cachedPos.longitude)],
                    true
                )
            }
            if (!this.liveMarker) {
                this.isLiveLocationLoading = true
            }

            try {
                await this.$store.dispatch('fetchDriverLocation', String(this.liveDriver.id))

                const locations = this.$store.getters['driverLocations']
                const pos = locations ? locations[this.liveDriver.id] : null

                if (pos && pos.latitude && pos.longitude) {
                    this.applyLiveLatLng([Number(pos.latitude), Number(pos.longitude)], false)
                }
            } catch (err) {
                console.warn('[Timesheet] Live location fetch failed:', err)
            } finally {
                this.isLiveLocationLoading = false
            }
        },
        startLiveTracking() {

            if (this.liveLocationInterval) return

            this.isTrackingLive = true
            this.fetchLiveLocation() 

            this.liveLocationInterval = setInterval(() => {
                this.fetchLiveLocation()
            }, LIVE_POLL_INTERVAL_MS)
        },
        stopLiveTracking() {
            if (this.liveLocationInterval) {
                clearInterval(this.liveLocationInterval)
                this.liveLocationInterval = null
            }

            if (this.liveMarker && this._map) {
                this._map.removeLayer(this.liveMarker)
                this.liveMarker = null
            }

            this.isTrackingLive = false
            this.hasCenteredOnLive = false
            this.isLiveLocationLoading = false
        },
        syncLiveTracking() {
            if (this.isDriverWorking) {
                this.startLiveTracking()
            } else {
                this.stopLiveTracking()
            }
        },

        getStatusClass(status) {
            if (status === 'Approved') return 'status-approved'
            if (status === 'Pending') return 'status-pending'
            return 'status-default'
        },

        getProgressClass(progress) {
            if (progress === 'Pay Approved') return 'progress-approved'
            if (progress === 'Submitted') return 'progress-submitted'
            if (progress === 'Not Submitted') return 'progress-not-submitted'
            if (progress === 'In Work') return 'in-work'
            return 'progress-default'
        },

        dateRangeText(date_range_selected) {
            return DATE_RANGE_HELPERS.dateRangeText(date_range_selected)
        },
        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : '-'
        },
        isPending() {
            return this.selectedTimesheetFull &&
                this.selectedTimesheetFull.status === TIMESHEET_STATUS.PENDING
        },
        isApproved() {
            return this.selectedTimesheetFull &&
                this.selectedTimesheetFull.status === TIMESHEET_STATUS.APPROVED
        },
        isDiscarded() {
            return this.selectedTimesheetFull &&
                this.selectedTimesheetFull.status === TIMESHEET_STATUS.DISCARDED
        },
        formatTime(field) {
            let val = this.form[field]
            if (!val) return

            val = String(val).replace(/[^\d:]/g, '')

            if (!val.includes(':')) {
                if (val.length === 3) val = '0' + val
                if (val.length === 4) {
                    val = val.slice(0, 2) + ':' + val.slice(2)
                }
            }

            const [h, m] = val.split(':').map(Number)

            if (isNaN(h) || isNaN(m) || h > 23 || m > 59) {
                this.form[field] = null
                return
            }

            this.form[field] =
                String(h).padStart(2, '0') +
                ':' +
                String(m).padStart(2, '0')
        },
        onTimeFocus(field) {
            if (!this.form[field]) {
                const now = new Date()
                const roundedMinutes = Math.floor(now.getMinutes() / 15) * 15

                this.form[field] =
                    String(now.getHours()).padStart(2, '0') +
                    ':' +
                    String(roundedMinutes).padStart(2, '0')
            }
        },
        calculateBonus() {
            this.form.const_fuel_bonus =
                (Number(this.form.fuel_bonus_0) * this.fuel_bonus_0_default) +
                (Number(this.form.fuel_bonus_3) * this.fuel_bonus_3_default) +
                (Number(this.form.fuel_bonus_6) * this.fuel_bonus_6_default)
        },
        buildTimesheetPayload(extraStatus) {
            return {
                driver_start_time: this.form.start_time,
                driver_finish_time: this.form.finish_time,
                driver_comment: this.form.comment,
                comment: this.form.comment,
                cost_bonus: this.form.cost_bonus,
                const_fuel_bonus: this.form.const_fuel_bonus,
                fuel_bonus_0: this.form.fuel_bonus_0,
                fuel_bonus_3: this.form.fuel_bonus_3,
                fuel_bonus_6: this.form.fuel_bonus_6,
                ...extraStatus,
            }
        },

        async approveTimesheet() {
            if (this.isRowLocked) return

            try {
                await this.$store.dispatch('updateDrivershareTimesheet', {
                    id: this.selectedTimesheetFull.id,
                    ...this.buildTimesheetPayload({
                        status: TIMESHEET_STATUS.APPROVED,
                        status_progress: TIMESHEET_PROGRESS.PAY_APPROVED,
                    }),
                })

                this.$toast.success('Timesheet approved successfully')
            } catch (e) {
                console.error(e)
            }
        },
        async discardTimesheet() {
            if (this.isRowLocked) return

            try {
                await this.$store.dispatch('updateDrivershareTimesheet', {
                    id: this.selectedTimesheetFull.id,
                    ...this.buildTimesheetPayload({
                        status: TIMESHEET_STATUS.DISCARDED,
                        status_progress: TIMESHEET_PROGRESS.DISCARDED,
                    }),
                })

                this.$toast.success('Timesheet discarded successfully')
            } catch (e) {
                console.error(e)
            }
        },
        async unapprovePay() {
            if (this.isRowLocked) return

            await this.$store.dispatch('updateDrivershareTimesheet', {
                id: this.selectedTimesheetFull.id,
                status: TIMESHEET_STATUS.APPROVED,
                status_progress: TIMESHEET_PROGRESS.TIME_APPROVED,
            })
        },

        async unapproveTime() {
            if (this.isRowLocked) return

            await this.$store.dispatch('updateDrivershareTimesheet', {
                id: this.selectedTimesheetFull.id,
                status: TIMESHEET_STATUS.PENDING,
                status_progress: TIMESHEET_PROGRESS.SUBMITED,
            })
        },
        async removeTimesheet() {
            if (this.isRowLocked) {
                return
            }

            if (!confirm('Remove this timesheet?')) {
                return
            }

            try {
                await this.$store.dispatch('removeDrivershareTimesheet', this.selectedTimesheetFull)
                this.selectedTimesheet = null
                this.stopLiveTracking()
                this.$toast.success('Timesheet removed')
            } catch (e) {
                console.error(e)
                this.$toast.error('Failed to remove timesheet')
            }
        },
        toggleSelect(item) {
            if (item.id <= 0) return
            const exists =
                this.selectedTimesheetIds.includes(item.id)

            if (exists) {
                this.selectedTimesheetIds =
                    this.selectedTimesheetIds.filter(
                        id => id !== item.id
                    )
            } else {
                this.selectedTimesheetIds.push(item.id)
            }
        },
        async approveSelectedTimesheets() {
            if (!this.selectedTimesheetIds.length) {
                return
            }

            try {
                let requests = []
                let skipped = 0
                this.selectedTimesheetIds.forEach(
                    rowId => {
                        const driverTs =
                            this.allTimesheets.find(
                                t => t.id === rowId
                            )
                        if (!driverTs) return
                        if (driverTs.id <= 0) {
                            skipped++
                            return
                        }

                        const grouped =
                            this.allTimesheets.filter(
                                t => t.shift_id === driverTs.shift_id && t.id > 0
                            )
                        grouped.forEach(ts => {
                            requests.push(
                                this.$store.dispatch(
                                    'updateDrivershareTimesheet',
                                    {
                                        id: ts.id,
                                        status: TIMESHEET_STATUS.APPROVED,
                                        status_progress: TIMESHEET_PROGRESS.PAY_APPROVED,
                                    }
                                )
                            )
                        })
                    }
                )
                await Promise.all(requests)

                if (requests.length) {
                    this.$toast.success(
                        `${this.selectedTimesheetIds.length - skipped} grouped timesheets approved`
                    )
                }
                if (skipped) {
                    this.$toast.error(
                        `${skipped} shift(s) skipped — driver hasn't clocked in yet`
                    )
                }
                this.selectedTimesheetIds = []

            } catch (e) {
                console.error(e)

                this.$toast.error(
                    'Failed to approve timesheets'
                )
            }
        },
        async discardSelectedTimesheets() {
            if (!this.selectedTimesheetIds.length) {
                return
            }
            try {
                let requests = []
                let skipped = 0

                this.selectedTimesheetIds.forEach(rowId => {
                    const driverTs =
                        this.allTimesheets.find(
                            t => t.id === rowId
                        )

                    if (!driverTs) return
                    if (driverTs.id <= 0) {
                        skipped++
                        return
                    }

                    const grouped =
                        this.allTimesheets.filter(
                            t => t.shift_id === driverTs.shift_id && t.id > 0
                        )

                    grouped.forEach(ts => {
                        requests.push(
                            this.$store.dispatch(
                                'updateDrivershareTimesheet',
                                {
                                    id: ts.id,
                                    status: TIMESHEET_STATUS.DISCARDED,
                                    status_progress: TIMESHEET_PROGRESS.DISCARDED,
                                }
                            )
                        )
                    })
                })

                await Promise.all(requests)

                if (requests.length) {
                    this.$toast.success(
                        `${this.selectedTimesheetIds.length - skipped} grouped timesheets discarded`
                    )
                }
                if (skipped) {
                    this.$toast.error(
                        `${skipped} shift(s) skipped — driver hasn't clocked in yet`
                    )
                }
                this.selectedTimesheetIds = []

            } catch (e) {
                console.error(e)

                this.$toast.error( 'Failed to discard timesheets')
            }
        },
    },
}
</script>
<style scoped>
.timesheet-wrapper {
    height: calc(100vh - 64px);
    background: #f5f7fa;
}

.fill-height {
    height: 100%;
}

/* ============= LEFT PANEL ============= */
.left-panel-custom {
    max-width: 230px;
    background: #ffffff;
    border-right: 1px solid #e0e0e0;
    overflow-y: auto;
    height: 100%;
}

/* ============= RIGHT PANEL ============= */
.right-panel-custom {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

/* ============= TOP TOOLBAR ============= */
.top-toolbar {
    min-height: 68px !important;
    padding: 10px 12px !important;
    flex: none;
    margin-top: 10px;
}

.top-toolbar ::v-deep .v-toolbar__content {
    height: 58px !important;
    padding: 0 !important;
}

.top-toolbar .d-flex.align-center {
    gap: 10px;
}

.location-autocomplete {
    max-width: 230px;
}

.select-drop-item-sub_restaurant {
    padding-left: 4px;
}

.date-pill {
    background: #e5e7eb;
    border-radius: 999px;
    padding: 2px 8px;
    height: 40px;
}

.date-pill .v-btn {
    min-width: 24px !important;
}

.date-pill .v-icon {
    color: #2563eb !important;
    /* blue */
}

.pill-select {
    max-width: 140px;
    height: 40px;
}

.pill-select ::v-deep .v-input__control {
    border-radius: 999px;
}

.pill-select ::v-deep .v-input__slot {
    min-height: 32px !important;
    height: 32px !important;
    display: flex;
    align-items: center;
}

.pill-select.active ::v-deep .v-input__slot {
    background: #e0f2fe !important;
}

.pill-select ::v-deep .v-icon {
    color: #2563eb !important;
}

.pill-btn {
    border-radius: 999px !important;
    text-transform: none !important;
    font-size: 16px;

    background: transparent !important;
    box-shadow: none !important;
}

.pill-btn.v-btn {
    border: 1px solid #d1d5db !important;
    height: 40px;
}

.pill-btn.orange {
    border-color: #fb923c !important;
    color: #ea580c !important;
}

.pill-btn.green {
    border-color: #34d399 !important;
    color: #059669 !important;
}

.primary-pill {
    background: #1e3a8a !important;
    color: white !important;
    border-radius: 999px !important;
}
/* ============= TABLE ============= */
.table-summary-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
}

.timesheet-content {
    flex: 1;
    overflow-y: auto;
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
    transition: flex 0.3s ease;
    margin-top: 10px;
}

.timesheet-content.table-collapsed {
    flex: 0 0 auto;
    max-height: 340px;
    border-bottom: 2px solid #e5e7eb;
}

.timesheet-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}

.timesheet-table ::v-deep th {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    color: #374151;
    font-weight: 600;
    font-size: 14px;
    padding: 8px 10px !important;
    height: 36px !important;
}
.timesheet-table ::v-deep td {
    height: 34px !important;
    padding: 2px 10px !important;
    line-height: 1.1 !important;
    white-space: nowrap;
    vertical-align: middle;
}

.timesheet-row {
    border-bottom: 1px solid #e5e7eb;
    cursor: pointer;
    height: 34px;
}

.timesheet-row:hover {
    background: #f9fafb;
}
/* Virtual (not-yet-submitted) rows — no DB entry exists yet */
.timesheet-row.virtual-row td {
    color: #9ca3af;
    font-style: italic;
}

.restaurant-child-row {
    height: 30px;
}

.restaurant-child-row td {
    font-size: 12px;
    color: #6b7280;
    background: #fffdf5;
    border-top: 1px solid #f3f4f6;
}

.timesheet-row td {
    font-size: 13px;
    color: #374151;
}

.custom-chip {
    border-radius: 12px !important;
    height: 20px !important;
    font-size: 11px !important;
}

.status-cell,
.progress-cell {
    text-align: center;
}

.status-approved {
    background: #00ff1a !important;
    color: #000000 !important;
}

.status-pending {
    background: #ef8700 !important;
    color: #ffffff !important;
}

.progress-approved {
    background: #00ff7b !important;
    color: #ffffff !important;
}

.in-work {
    background: #00b3ff !important;
    color: #ffffff !important;
}

.progress-submitted {
    background: #ef8700 !important;
    color: #ffffff !important;
}
.progress-not-submitted {
    background: #9ca3af !important;
    color: #ffffff !important;
}

.earning-cell {
    text-align: center !important;
    width: 140px;
}

/* Earnings now laid out in a single row instead of stacked lines */
.earning-breakdown {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    white-space: nowrap;
    gap: 4px;
}

.base-earning {
    color: #374151;
    font-weight: 500;
    font-size: 10px;
}

.bonus-earning {
    color: #059669;
    font-size: 10px;
}

.total-earning {
    font-weight: 700;
    color: #111827;
    border-top: none;
    padding-left: 4px;
    border-left: 1px solid #e5e7eb;
    min-width: auto;
    text-align: center;
    font-size: 10px;
}
/* Checkbox + expand button stay compact inside the single-line row */
.timesheet-table ::v-deep .v-input--checkbox {
    margin: 0 !important;
    padding: 0 !important;
}

.timesheet-table ::v-deep .v-input--checkbox .v-input__slot {
    min-height: 0 !important;
}

.action-cell ::v-deep .v-btn {
    height: 28px !important;
    width: 28px !important;
}

/* ============= SUMMARY SECTION ============= */

.section-disabled .summary-left,
.section-disabled .button-grid {
    pointer-events: none;
    opacity: 0.6;
}

.summary-section {
    background: #fff;
    border-top: 1px solid #e5e7eb;
    border-radius: 0 0 16px 16px;
    margin-top: 4px;
}

.summary-header {
    background: #edf9ff;
    padding: 8px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.summary-header h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #0f2b57;
}

.summary-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.header-pill {
    border-radius: 999px !important;
    text-transform: none !important;
    color: #0f2b57 !important;
    border: 1px solid #b8d9e8 !important;
    background: white !important;
    height: 28px !important;
    font-size: 12px !important;
}
.virtual-badge {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border-radius: 20px;
    padding: 4px 10px;
}

/* Live tracking badge */
.live-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: #7e22ce;
    background: #f3e8ff;
    border-radius: 20px;
    padding: 4px 10px;
}

.live-badge .live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #a855f7;
    animation: live-pulse 1.5s ease-in-out infinite;
}

@keyframes live-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(168, 85, 247, 0.5); }
    50% { box-shadow: 0 0 0 5px rgba(168, 85, 247, 0); }
}

.summary-body {
    display: grid;
    grid-template-columns: 56% 44%;
    gap: 14px;
    padding: 12px 16px;
}

.summary-left {
    width: 100%;
}

.summary-right {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* schedule box */
.schedule-box {
    background: #f7f0d7;
    border-radius: 12px;
    padding: 10px 16px;
    display: flex;
    gap: 18px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.schedule-item {
    display: flex;
    flex-direction: column;
}

.schedule-item span {
    font-size: 11px;
    color: #44546a;
}

.schedule-item strong {
    margin-top: 2px;
    font-size: 14px;
    font-weight: 700;
    color: #0f2b57;
}

/* form */
.summary-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
}

.summary-form-grid label,
.milestone-item label,
.comment-section label {
    display: block;
    margin-bottom: 3px;
    font-size: 12px;
    font-weight: 700;
    color: #102d59;
}

.summary-form-grid ::v-deep .v-input__slot,
.milestone-item ::v-deep .v-input__slot,
.comment-section ::v-deep .v-input__slot {
    background: #f7f7f7 !important;
    border: 1px solid #dfdfdf !important;
    border-radius: 10px !important;
    box-shadow: none !important;
    min-height: 34px !important;
}

/* milestone */
.milestone-row {
    display: flex;
    gap: 10px;
    margin-top: 8px;
}

.milestone-item {
    flex: 1;
}

.milestone-item ::v-deep input {
    text-align: center;
    font-weight: 600;
}

/* comment */
.comment-section {
    margin-top: 8px;
}

.comment-section ::v-deep .v-input__slot {
    min-height: 56px !important;
}

/* map */
.map-box-wrapper {
    position: relative;
    height: 320px;
    border-radius: 14px;
    overflow: hidden;
}

.map-box {
    height: 320px;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    background: #f8f8f8;
}

.map-loading-overlay {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 5px 12px;
    font-size: 11px;
    font-weight: 600;
    color: #44546a;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    pointer-events: none;
}

.map-loading-spinner {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #dbeafe;
    border-top-color: #2563eb;
    animation: map-spin 0.7s linear infinite;
}

@keyframes map-spin {
    to { transform: rotate(360deg); }
}

/* buttons */
.button-grid {
    margin-top: 8px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}

.button-grid .v-btn {
    height: 38px !important;
    border-radius: 999px !important;
    text-transform: none !important;
    font-size: 13px;
    font-weight: 600;
}

.btn-remove {
    background: #f2f2f2 !important;
    color: #666 !important;
}

.btn-discard {
    background: #fdeaea !important;
    color: #ef4444 !important;
}

.btn-approve {
    grid-column: span 2;
    background: #08b8ea !important;
    color: white !important;
}

.btn-pay {
    background: #f8ecdc !important;
    color: #d97706 !important;
}

.btn-time {
    background: #e9eef8 !important;
    color: #1e3a8a !important;
}
.button-grid .v-btn.v-btn--disabled {
    background: #e5e7eb !important;
    color: #9ca3af !important;
    opacity: 1 !important;
    box-shadow: none !important;
}

/* ============= SCROLLBAR ============= */
.left-panel-custom::-webkit-scrollbar,
.timesheet-content::-webkit-scrollbar {
    width: 6px;
}

.left-panel-custom::-webkit-scrollbar-thumb,
.timesheet-content::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
}

.left-panel-custom::-webkit-scrollbar-thumb:hover,
.timesheet-content::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}
.selected-row td {
    background: #dbeafe !important;
    border-top: 1px solid #93c5fd !important;
    border-bottom: 1px solid #93c5fd !important;
}
</style>